// DailyCeiling — the daily AI cost ceilings (QA-14): one per account, one
// per deployment. Before every AI call the recording agent reads today's
// spend from the ledger and asks `decide`; at or past a ceiling the call is
// refused and the refusal names the ceiling and when it resets.
//
// Pure. The shell reads the spend; this only decides.
//
// Race bound: spend is what the ledger holds, and a call is recorded when
// it ends. So calls already in flight when a ceiling is crossed still
// finish: the overshoot is at most one call per call in flight, and every
// call started after the crossing is refused.
export { billedToOf, DAY_BOUNDARY_UTC_OFFSET_MS, DEFAULT_CEILINGS, dayStartOf, decide, nextResetOf, parseReachedDetail, reachedDetailOf, };
import { mk as mkTimestamp } from "../timestamp.js";
// QA-14's proposed defaults: USD 5 per account, USD 50 per deployment.
const DEFAULT_CEILINGS = {
    accountMicroUsd: 5_000_000,
    deploymentMicroUsd: 50_000_000,
};
const billedToOf = (initiator, workspaceId) => initiator.type === "user"
    ? { type: "billed_to_user", userId: initiator.userId }
    : workspaceId !== undefined
        ? { type: "billed_to_workspace_owner", workspaceId }
        : { type: "billed_to_deployment" };
// The day turns at midnight UTC (an assumption the owner may change: this
// offset is the one place to change it).
const DAY_BOUNDARY_UTC_OFFSET_MS = 0;
const DAY_MS = 86_400_000;
const dayStartOf = (now) => mkTimestamp(Math.floor((now - DAY_BOUNDARY_UTC_OFFSET_MS) / DAY_MS) * DAY_MS +
    DAY_BOUNDARY_UTC_OFFSET_MS);
const nextResetOf = (now) => mkTimestamp(dayStartOf(now) + DAY_MS);
// Refuse when today's spend has reached a ceiling (at counts as reached).
// The account ceiling is named first: it is the one the Author can act on.
const decide = (ceilings, spend, now) => {
    const reached = (scope, limitMicroUsd) => ({
        type: "ceiling_reached",
        scope,
        limitMicroUsd,
        resetsAt: nextResetOf(now),
    });
    if (spend.accountMicroUsd !== undefined &&
        spend.accountMicroUsd >= ceilings.accountMicroUsd)
        return reached("account", ceilings.accountMicroUsd);
    if (spend.deploymentMicroUsd >= ceilings.deploymentMicroUsd)
        return reached("deployment", ceilings.deploymentMicroUsd);
    return { type: "within_ceilings" };
};
// A refusal as the one-line `detail` a recorded turn failure carries
// ("account 5000000 1762300800000": scope, limit, reset in epoch ms), and
// its parse at the reading boundary: anything else is not a refusal.
const reachedDetailOf = (r) => `${r.scope} ${r.limitMicroUsd} ${r.resetsAt}`;
const DETAIL = /^(account|deployment) (\d{1,12}) (\d{1,15})$/;
const parseReachedDetail = (raw) => {
    const m = typeof raw === "string" ? DETAIL.exec(raw) : null;
    const scope = m?.[1];
    return m === null || (scope !== "account" && scope !== "deployment")
        ? undefined
        : {
            type: "ceiling_reached",
            scope,
            limitMicroUsd: Number(m[2]),
            resetsAt: mkTimestamp(Number(m[3])),
        };
};
//# sourceMappingURL=dailyCeiling.js.map